import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import './Login.css';

const CLINIC_IMAGES = [
  { src: '/assets/clinic/sala-espera-principal.jpeg', alt: 'Sala de espera principal' },
  { src: '/assets/clinic/quirofano-equipo-anestesia.jpeg', alt: 'Quirofano con equipo de anestesia' },
  { src: '/assets/clinic/ambulancias-exterior-urgencias.jpeg', alt: 'Ambulancias en acceso de urgencias' },
  { src: '/assets/clinic/ambulancia-frontal-urgencias.jpeg', alt: 'Ambulancia de urgencias basicas' },
  { src: '/assets/clinic/camilla-interior-ambulancia.jpeg', alt: 'Camilla e interior de ambulancia' },
  { src: '/assets/clinic/ambulancia-puertas-traseras.jpeg', alt: 'Puertas traseras de ambulancia' },
  { src: '/assets/clinic/ambulancia-rotulada-clinica.jpeg', alt: 'Ambulancia rotulada de Clínica de Especialidades Guarneros' },
  { src: '/assets/clinic/quirofano-sala-operaciones.jpeg', alt: 'Sala de operaciones equipada' },
  { src: '/assets/clinic/quirofano-vista-general.jpeg', alt: 'Vista general de quirofano' },
  { src: '/assets/clinic/pasillo-area-urgencias.jpeg', alt: 'Pasillo del area de urgencias' },
];

const SLIDE_INTERVAL = 5000;

const ClinicGallery = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback((index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setPrevIndex(activeIndex);
    setActiveIndex(index);
    setTimeout(() => {
      setPrevIndex(null);
      setIsAnimating(false);
    }, 800);
  }, [activeIndex, isAnimating]);

  const goNext = useCallback(() => {
    goTo((activeIndex + 1) % CLINIC_IMAGES.length);
  }, [activeIndex, goTo]);

  useEffect(() => {
    const timer = setInterval(goNext, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [goNext]);

  return (
    <div className="gallery-panel" aria-label="Galería de instalaciones">
      <div className="gallery-slides">
        {CLINIC_IMAGES.map((img, i) => (
          <div
            key={img.src}
            className={[
              'gallery-slide',
              i === activeIndex ? 'gallery-slide--active' : '',
              i === prevIndex ? 'gallery-slide--exit' : '',
            ].filter(Boolean).join(' ')}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading={i === 0 ? 'eager' : 'lazy'}
              draggable={false}
            />
            <div className="gallery-slide-overlay" />
          </div>
        ))}
      </div>

      {/* Brand overlay */}
      <div className="gallery-brand">
        <div className="gallery-brand-logo">
          <img src="/assets/logos/logo-full.jpeg" alt="Clínica de Especialidades Guarneros" />
        </div>
        <p className="gallery-brand-tagline">
          Salud, confianza y tecnología<br />a tu servicio
        </p>
        <div className="gallery-stats">
          <div className="gallery-stat">
            <span className="gallery-stat-number">+15</span>
            <span className="gallery-stat-label">Años de experiencia</span>
          </div>
          <div className="gallery-stat-divider" aria-hidden="true" />
          <div className="gallery-stat">
            <span className="gallery-stat-number">+5K</span>
            <span className="gallery-stat-label">Pacientes atendidos</span>
          </div>
          <div className="gallery-stat-divider" aria-hidden="true" />
          <div className="gallery-stat">
            <span className="gallery-stat-number">15</span>
            <span className="gallery-stat-label">Especialidades</span>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="gallery-dots" role="tablist" aria-label="Navegación de galería">
        {CLINIC_IMAGES.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === activeIndex}
            aria-label={`Imagen ${i + 1}`}
            className={`gallery-dot${i === activeIndex ? ' gallery-dot--active' : ''}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="gallery-progress" aria-hidden="true">
        <div key={activeIndex} className="gallery-progress-bar" />
      </div>
    </div>
  );
};

const Login = ({ onLoginSuccess, onBack }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Doctor autenticado pendiente de cambiar contraseña (primer ingreso)
  const [pendingDoctor, setPendingDoctor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.login(username, password);
      if (result.success) {
        if (result.data.must_change_password) {
          setPendingDoctor(result.data.doctor);
        } else {
          onLoginSuccess(result.data.doctor);
        }
      } else {
        setError(result.message || 'Error al iniciar sesión');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Error al conectar con el servidor. Verifique su conexión.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword === password) {
      setError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.changePassword(password, newPassword);
      if (result.success) {
        onLoginSuccess(pendingDoctor);
      } else {
        setError(result.message || 'Error al cambiar la contraseña');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Error al conectar con el servidor. Verifique su conexión.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      <ClinicGallery />

      <div className="login-panel">
        <div className="login-panel-inner">
          {/* Mobile-only brand */}
          <div className="login-mobile-brand" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="22" stroke="#2563eb" strokeWidth="2"/>
              <path d="M24 12v24M12 24h24" stroke="#2563eb" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            <span>Clínica de Especialidades Guarneros</span>
          </div>

          {onBack && (
            <button className="login-back-btn" onClick={onBack} type="button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Volver al inicio
            </button>
          )}

          {pendingDoctor ? (
          <>
          <div className="login-header">
            <h1>Cambie su contraseña</h1>
            <p>Por seguridad, defina una contraseña nueva antes de continuar</p>
          </div>

          <form onSubmit={handleChangePassword} className="login-form" noValidate>
            {error && (
              <div className="login-error" role="alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            <div className="form-field">
              <label htmlFor="new-password">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Nueva contraseña
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
                disabled={loading}
                autoFocus
                autoComplete="new-password"
              />
            </div>

            <div className="form-field">
              <label htmlFor="confirm-password">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Confirmar contraseña
              </label>
              <div className="input-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita la nueva contraseña"
                  required
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !newPassword || !confirmPassword}
            >
              {loading ? (
                <>
                  <span className="login-btn-spinner" aria-hidden="true" />
                  Guardando...
                </>
              ) : (
                <>
                  Guardar y continuar
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>
          </>
          ) : (
          <>
          <div className="login-header">
            <h1>Bienvenido</h1>
            <p>Ingrese sus credenciales para acceder al sistema</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {error && (
              <div className="login-error" role="alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            <div className="form-field">
              <label htmlFor="username">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Usuario
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingrese su usuario"
                required
                disabled={loading}
                autoFocus
                autoComplete="username"
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <div className="form-field">
              <label htmlFor="password">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Contraseña
              </label>
              <div className="input-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingrese su contraseña"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !username || !password}
            >
              {loading ? (
                <>
                  <span className="login-btn-spinner" aria-hidden="true" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  Iniciar Sesión
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>
          </>
          )}

          <p className="login-footer-text">
            &copy; {new Date().getFullYear()} Clínica de Especialidades Guarneros &mdash; Sistema de Consulta Externa
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
